import { Zeeguu_API } from "./classDef";
import qs from "qs";

Zeeguu_API.prototype.getOneTranslation = function (
  from_lang,
  to_lang,
  word,
  bookmark_token_i,
  context,
  context_token_i,
  sourceID,
  leftEllipsis,
  rightEllipsis,
  contextIdentifier,
  translationSource = 'reading',
  browsingSessionId = null,
  readingSessionId = null,
  isMweExpression = false,
  isSeparatedMwe = false,
  fullSentenceContext = null,
  mwePartnerTokenI = null,
) {
  console.log(`[TRANSLATION] getOneTranslation called`, {
    timestamp: new Date().toISOString(),
    word,
    from_lang,
    to_lang,
    translationSource,
    contextLength: context?.length || 0,
    isMweExpression,
  });

  let w_sent_i, w_token_i, w_total_tokens;
  let c_paragraph_i, c_sent_i, c_token_i;
  [w_sent_i, w_token_i, w_total_tokens] = bookmark_token_i;
  if (context_token_i) [c_paragraph_i, c_sent_i, c_token_i] = context_token_i;

  let payload = {
    word: word,
    context: context,
    source_id: sourceID,
    w_sent_i: w_sent_i,
    w_token_i: w_token_i,
    w_total_tokens: w_total_tokens,
    c_paragraph_i: c_paragraph_i,
    c_sent_i: c_sent_i,
    c_token_i: c_token_i,
    left_ellipsis: leftEllipsis,
    right_ellipsis: rightEllipsis,
    context_identifier: contextIdentifier,
    translation_source: translationSource,
    browsing_session_id: browsingSessionId,
    reading_session_id: readingSessionId,
    is_mwe_expression: isMweExpression,
    is_separated_mwe: isSeparatedMwe,
    full_sentence_context: fullSentenceContext,
    mwe_partner_token_i: mwePartnerTokenI,
  };

  console.log(`[TRANSLATION] About to call apiPost`, { timestamp: new Date().toISOString(), payloadSize: JSON.stringify(payload).length });

  // ADR 022: canonical name. /get_one_translation is still aliased on the
  // server for the extension's frozen bundle; once that ships its next build,
  // the alias can go away.
  return this.apiPost(`/translate_word/${from_lang}/${to_lang}`, payload);
};


// Explain what a selection means in its sentence, for the Explain option in
// AlterMenu. Deliberately does NOT send the translation the learner was shown:
// handing the model an ambiguous gloss made it reason backwards and invent
// morphology to fit. The server caches on (selection, context, languages,
// level), so a re-tap is free.
Zeeguu_API.prototype.explainSelection = function (from_lang, to_lang, selection, context, cefr_level) {
  return this.apiPost(`/explain_selection/${from_lang}/${to_lang}`, {
    selection: selection,
    context: context,
    cefr_level: cefr_level,
  }).then((response) => response.data);
};

// ADR 022 follow-up: lazy alternatives for words whose /translate_word
// response didn't include them (own-past-translation early-return path).
// Called from AlterMenu open, not on every word tap.
Zeeguu_API.prototype.getTranslationAlternatives = function (from_lang, to_lang, word, context) {
  return this.apiPost(`/translation_alternatives/${from_lang}/${to_lang}`, {
    word: word,
    context: context,
  }).then((response) => response.data);
};

// The translators disagreed on a lookup: an LLM settles it in the sentence.
// Answers { translation, reason, changed }; `changed` means the bookmark
// moved to `translation` (never when the learner changed it meanwhile).
Zeeguu_API.prototype.resolveTranslationDisagreement = function (
  from_lang,
  to_lang,
  word,
  context,
  candidates,
  shown,
  bookmark_id,
) {
  return this.apiPost(`/resolve_translation_disagreement/${from_lang}/${to_lang}`, {
    word: word,
    context: context,
    candidates: candidates,
    shown: shown,
    bookmark_id: bookmark_id,
  }).then((response) => response.data);
};

Zeeguu_API.prototype.getMultipleTranslations = function (
  from_lang,
  to_lang,
  word,
  context,
  numberOfResults,
  serviceToExclude,
  translationToExclude,
  articleID,
  isSeparatedMwe = false,
  fullSentenceContext = null,
) {
  let payload = {
    word: word,
    context: context,
    numberOfResults: numberOfResults,
    articleID: articleID,
    is_separated_mwe: isSeparatedMwe,
  };
  if (fullSentenceContext) {
    payload["full_sentence_context"] = fullSentenceContext;
  }
  if (serviceToExclude) {
    payload["service"] = serviceToExclude;
  }

  if (translationToExclude) {
    payload["currentTranslation"] = translationToExclude;
  }

  return this._post(
    `get_multiple_translations/${from_lang}/${to_lang}`,
    qs.stringify(payload),
  );
};

Zeeguu_API.prototype.contributeTranslation = function (
  from_lang,
  to_lang,
  word,
  translation,
  context,
  pageUrl,
  pageTitle,
) {
  /* Currently (13-01-2025), unused in the frontend */
  let payload = {
    word: word,
    translation: translation,
    context: context,
    url: pageUrl,
    pageTitle: pageTitle,
  };
  return this._post(
    `contribute_translation/${from_lang}/${to_lang}`,
    qs.stringify(payload),
  );
};

// Simple translation-only update (from reader alternative selection)
// Preserves all position data
Zeeguu_API.prototype.updateBookmarkTranslation = async function (
  bookmark_id,
  translation,
) {
  return await this.apiPost(`/update_bookmark_translation/${bookmark_id}`, {
    translation: translation,
  });
};

// Full bookmark update (from WordEditForm - can change word, context, translation)
Zeeguu_API.prototype.updateBookmark = async function (
  bookmark_id,
  word,
  translation,
  context,
  context_identifier,
) {
  let payload = {
    word: word,
    translation: translation,
    context: context,
    context_identifier: context_identifier,
  };

  return await this.apiPost(`/update_bookmark/${bookmark_id}`, payload);
};

Zeeguu_API.prototype.basicTranlsate = function (from_lang, to_lang, phrase) {
  let url = this._appendSessionToUrl(`basic_translate/${from_lang}/${to_lang}`);
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: `phrase=${phrase}`,
  });
};

Zeeguu_API.prototype.getTranslationHistory = function (limit = 50) {
  return this.apiGet(`/translation_history?limit=${limit}`).then((res) => res.data);
};

Zeeguu_API.prototype.logTranslationSearch = function (searchWord) {
  return this._post("log_translation_search", `search_word=${encodeURIComponent(searchWord)}`);
};
